import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const MOBILE_MEDIA_QUERY = '(max-width: 1023px)';
const SWIPE_THRESHOLD_PX = 64;
/** Max. sichtbare Verschiebung (px), wirkt mit Widerstand */
const MAX_VISUAL_OFFSET_PX = 72;
const DRAG_RESISTANCE = 0.55;
const RELEASE_TRANSITION = 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1)';

function isInsideHorizontalScrollContainer(element: Element | null): boolean {
  let current: Element | null = element;
  while (current !== null) {
    const ox = window.getComputedStyle(current).overflowX;
    if (ox === 'auto' || ox === 'scroll') {
      return true;
    }
    current = current.parentElement;
  }
  return false;
}

function setSwipeVisualOffset(el: HTMLElement, offsetPx: number, withReleaseTransition: boolean): void {
  if (withReleaseTransition) {
    el.style.transition = RELEASE_TRANSITION;
  } else {
    el.style.transition = 'none';
  }
  if (offsetPx <= 0) {
    el.style.transform = '';
    el.style.removeProperty('will-change');
  } else {
    el.style.willChange = 'transform';
    el.style.transform = `translateX(${offsetPx}px)`;
  }
}

/**
 * Nur unterhalb von `lg` (1024px): bei klarer Wischbewegung nach rechts zur Route `to` navigieren.
 * Während des Ziehens folgt die Karte sichtbar nach rechts (Widerstand + Deckelung).
 */
export function useSwipeNavigateOnMobile(to: string) {
  const navigate = useNavigate();
  const [element, setElement] = useState<HTMLElement | null>(null);
  const isMobileRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const activePointerIdRef = useRef<number | null>(null);
  const skipSwipeRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    const update = (): void => {
      isMobileRef.current = mq.matches;
    };
    update();
    mq.addEventListener('change', update);
    return () => {
      mq.removeEventListener('change', update);
    };
  }, []);

  useEffect(() => {
    if (element === null) {
      return;
    }

    const onPointerDown = (e: PointerEvent): void => {
      if (!isMobileRef.current) {
        return;
      }
      const target = e.target;
      skipSwipeRef.current =
        target instanceof Element ? isInsideHorizontalScrollContainer(target) : false;
      if (skipSwipeRef.current) {
        return;
      }
      activePointerIdRef.current = e.pointerId;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      try {
        element.setPointerCapture(e.pointerId);
      } catch {
        // setPointerCapture kann in Edge-Cases fehlschlagen
      }
    };

    const onPointerMove = (e: PointerEvent): void => {
      if (activePointerIdRef.current !== e.pointerId) {
        return;
      }
      if (!isMobileRef.current) {
        return;
      }
      if (skipSwipeRef.current) {
        return;
      }

      const dx = e.clientX - startXRef.current;
      const dy = e.clientY - startYRef.current;

      if (dx <= 0) {
        setSwipeVisualOffset(element, 0, false);
        return;
      }
      if (Math.abs(dx) <= Math.abs(dy)) {
        setSwipeVisualOffset(element, 0, false);
        return;
      }

      const eased = Math.min(dx * DRAG_RESISTANCE, MAX_VISUAL_OFFSET_PX);
      setSwipeVisualOffset(element, eased, false);
    };

    const onPointerUp = (e: PointerEvent): void => {
      if (activePointerIdRef.current !== e.pointerId) {
        return;
      }
      activePointerIdRef.current = null;
      if (!isMobileRef.current) {
        return;
      }
      if (skipSwipeRef.current) {
        return;
      }

      const dx = e.clientX - startXRef.current;
      const dy = e.clientY - startYRef.current;

      const horizontalDominant = Math.abs(dx) > Math.abs(dy);
      const crossesThreshold = dx >= SWIPE_THRESHOLD_PX;
      const shouldNavigate = horizontalDominant && crossesThreshold;

      if (shouldNavigate) {
        navigate(to);
        setSwipeVisualOffset(element, 0, false);
        return;
      }

      setSwipeVisualOffset(element, 0, true);
    };

    const onPointerCancel = (e: PointerEvent): void => {
      if (activePointerIdRef.current === e.pointerId) {
        activePointerIdRef.current = null;
        setSwipeVisualOffset(element, 0, true);
      }
    };

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerup', onPointerUp);
    element.addEventListener('pointercancel', onPointerCancel);

    return () => {
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerUp);
      element.removeEventListener('pointercancel', onPointerCancel);
      setSwipeVisualOffset(element, 0, false);
    };
  }, [element, navigate, to]);

  const setRef = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  return setRef;
}
